import dateMath from '@elastic/datemath';
import { EuiSuperDatePicker } from '@elastic/eui';
import axios from 'axios';
import { cloneDeep, sortBy } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSearcData } from '../../services';
import {
	configureFieldStatus,
	saveAreaQuery,
	saveDate,
	saveImageKeys,
	saveImageLoadCount,
	saveMaskedFlag,
	saveSearchData,
	setDeleteRecordCount,
	setIsNoRecordsAvailable,
	setView,
} from '../../store/actions';
import { LAST_POINT_OF_FAILURE, RAW_LOGS, REDACTION_VIEW } from '../../utilities/constants';
import customTost from '../Notification';
import './dateTimePicker.scss';

const DateTimePicker = (props) => {
	const dispatch = useDispatch();
	const {
		indexValue,
		indexAlias,
		aliasFlag,
		mappingImageKey,
		screenSignature,
		configuredFields,
		observedAtDetail,
		selectedBucket,
		imageDetail,
		maskedBucket,
		view,
		envars,
		maskedFlag,
		isDeleteModeOn,
	} = useSelector((store) => store.storeProps);
	const {
		setFilterValue,
		setLowerRange,
		setHigherRange,
		setGraphdata,
		setQueryData,
		setSelectedOption,
		graphEvent,
		setGraphEvent,
		setIsLoading,
		isLoading,
		setEnableExport,
		queryString,
		setMapping,
		isDisabled,
		setClearFilter,
	} = props;
	const [start, setStart] = useState('now-15d');
	const [end, setEnd] = useState('now');
	const timezone = moment.tz.guess();

	useEffect(() => {
		try {
			if (graphEvent) {
				onTimeChange(graphEvent);
			}
		} catch (error) {
			console.error(error);
		}
		// eslint-disable-next-line
	}, [graphEvent]);

	useEffect(() => {
		try {
			let event = {};
			event.start =
				observedAtDetail && observedAtDetail.startDate ? observedAtDetail.startDate : start;
			event.end = observedAtDetail && observedAtDetail.endDate ? observedAtDetail.endDate : end;
			event.chart = false;
			event.map = true;
			onTimeChange(event);
		} catch (error) {
			console.error(error);
		}
		// eslint-disable-next-line
	}, [queryString, screenSignature, selectedBucket, maskedBucket, isDeleteModeOn === false]);

	// on query search
	const onTimeChange = (e) => {
		try {
			if (!e.map) {
				setMapping(true);
			}
			if (view === REDACTION_VIEW && maskedBucket === '') {
				dispatch(setView('carouselView'));
			} else {
				dispatch(
					setView(
						maskedBucket ? (view === REDACTION_VIEW ? REDACTION_VIEW : 'masked') : 'carouselView',
					),
				);
			}
			if (maskedFlag && maskedBucket) {
				// dispatch(setView('masked'));
				dispatch(saveMaskedFlag(false));
			}
			if (!isLoading) {
				setIsLoading(true);
			}
			let startDateVal = '';
			let endDateVal = '';
			if (e.chart) {
				startDateVal = e.start;
				endDateVal = e.end;
			} else {
				startDateVal = moment(dateMath.parse(e.start)).format('YYYY-MM-DDTHH:mm:ss');
				endDateVal = moment(
					dateMath.parse(
						e.end === 'now/d' || e.end === 'now/w' || e.end === 'now/M' || e.end === 'now/y'
							? 'now'
							: e.end,
					),
				).format(e.end === 'now-1d/d' ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm:ss');
				if (e.end === 'now-1d/d') {
					endDateVal = `${endDateVal}T23:59:59`;
				}
			}
			if (startDateVal && endDateVal) {
				startDateVal = moment.tz(startDateVal, timezone).format();
				endDateVal = moment.tz(endDateVal, timezone).format();
			}
			let elasticQuery = queryString.length ? queryString : [];
			let observedAt =
				startDateVal && endDateVal
					? {
							key: 'observed_at',
							value: `${startDateVal} AND ${endDateVal}`,
							operator: 'BETWEEN',
							operand: 'AND',
						}
					: '';
			if (envars?.storageMechanism === 'minio') {
				if (selectedBucket) {
					getData(e, elasticQuery, observedAt);
				}
			} else {
				getData(e, elasticQuery, observedAt);
			}
			if (observedAtDetail) {
				if (
					observedAtDetail.startDate !== startDateVal ||
					observedAtDetail.endDate !== endDateVal
				) {
					saveObservedAt(observedAt, startDateVal, endDateVal);
				}
			} else {
				saveObservedAt(observedAt, startDateVal, endDateVal);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const saveObservedAt = (observedAt, startDate, endDate) => {
		try {
			dispatch(
				saveDate({
					dateRange: observedAt,
					startDate: startDate,
					endDate: endDate,
				}),
			);
		} catch (error) {
			console.error(error);
		}
	};

	const getData = (e, q, date) => {
		try {
			dispatch(setIsNoRecordsAvailable({ noRecordsAvailable: false }));
			setEnableExport(false);
			setGraphEvent('');
			setStart(e.start);
			setEnd(e.end);
			setQueryData([]);
			dispatch(saveSearchData([]));
			dispatch(saveAreaQuery(''));
			dispatch(saveImageKeys([]));
			const payload = {
				body: JSON.stringify({
					index: indexValue,
					alias: aliasFlag ? indexAlias : '',
					groups: q,
					date: date,
					chart: e.chart,
					mappingKey: mappingImageKey,
					indexes: ['event_logs'],
				}),
			};
			/* istanbul ignore next */
			if (isDeleteModeOn) {
				setClearFilter && setClearFilter();
				axios
					.post(`/api/get-log-count`, payload)
					.then((res) =>
						dispatch(
							setDeleteRecordCount({
								deleteRecordCount: res.data || 0,
							}),
						),
					)
					.catch((error) => console.error(error));
			}
			getSearcData(payload)
				.then((resp) => {
					if (resp?.data?.preloads) {
						dispatch(saveImageLoadCount(resp.data.preloads));
					}
					if (resp?.data?.data) {
						const data = resp.data.data;
						setFilterValue([]);
						setLowerRange('');
						setHigherRange('');
						setEnableExport(true);
						if (mappingImageKey === '') {
							setIsLoading(false);
							setEnableExport(false);
							customTost({
								type: 'error',
								message: 'Please select image key',
							});
							return;
						}
						if (data?.aggregations?.images_count?.buckets) {
							if (data.aggregations.images_count.buckets.length > 0) {
								const graphArray = [];
								data.aggregations.images_count.buckets.forEach((obj) => {
									const graphObj = [];
									graphObj.push(obj.key, obj.doc_count);
									graphArray.push(graphObj);
								});
								setGraphdata(graphArray);
							} else {
								setGraphdata([]);
							}
						}
						if (data?.hits?.hits) {
							if (data.hits.hits.length > 0) {
								const sourceData = [];
								data.hits.hits.forEach((element) => {
									sourceData.push(element._source);
								});
								dispatch(saveSearchData(sourceData));
								dispatch(configureFieldStatus(true));
								setQueryData(sourceData);
								let esOptionsArray = [];
								if (imageDetail.length) {
									imageDetail.forEach((m, i) => {
										if (m && m.type !== 'nested') {
											esOptionsArray.push({
												id: i++,
												value: m.label,
												isChecked: false,
												type: m.type ? m.type : '',
											});
										}
									});
								}

								esOptionsArray = sortBy(esOptionsArray, 'value');
								const optionsObj = cloneDeep(esOptionsArray);
								optionsObj.forEach((option) => {
									option.isChecked = configuredFields.some((el) => el.label === option.value)
										? true
										: option.isChecked;
								});

								const filteredOptionsObj = optionsObj.filter(
									(val) => val?.value !== RAW_LOGS && val?.value !== LAST_POINT_OF_FAILURE,
								);
								setSelectedOption(filteredOptionsObj);
								const checkFieldStatus = optionsObj.filter((val) => val.isChecked === true);
								if (checkFieldStatus?.length) {
									dispatch(configureFieldStatus(true));
								} else {
									dispatch(configureFieldStatus(false));
								}
							} else {
								if (indexValue !== '') {
									setIsLoading(false);
									setEnableExport(false);
									customTost({
										type: 'error',
										message: 'No images found from search query',
									});
								}
							}
						}
					} else {
						customTost({
							type: 'error',
							description: 'Something went wrong. Please try later.',
							duration: 10,
						});
						setIsLoading(false);
						setEnableExport(false);
					}
				})
				.catch((error) => {
					if (error?.response?.data?.message && error?.response?.data?.statusCode !== 401) {
						customTost({
							type: 'error',
							description:
								error.response.data.message === 'Cannot read property "type" of undefined'
									? 'Please make sure you have selected the correct elastic index.'
									: 'Something went wrong. Please try later.',
							duration: 10,
						});
						setIsLoading(false);
						setEnableExport(false);
					}
				});
		} catch (error) {
			console.error(error);
			setIsLoading(false);
			setEnableExport(false);
		}
	};

	return (
		<EuiSuperDatePicker
			isDisabled={isDisabled}
			onTimeChange={onTimeChange}
			start={start}
			end={end}
			showUpdateButton={'iconOnly'}
		/>
	);
};

DateTimePicker.propTypes = {
	setFilterValue: PropTypes.func,
	setLowerRange: PropTypes.func,
	setHigherRange: PropTypes.func,
	setGraphdata: PropTypes.func,
	setQueryData: PropTypes.func,
	setSelectedOption: PropTypes.func,
	graphEvent: PropTypes.string,
	setGraphEvent: PropTypes.func,
	setIsLoading: PropTypes.func,
	isLoading: PropTypes.bool,
	setEnableExport: PropTypes.func,
	queryString: PropTypes.array,
	setMapping: PropTypes.func,
	isDisabled: PropTypes.bool,
	setClearFilter: PropTypes.func,
};

export default DateTimePicker;
